import mitt, {Emitter} from 'mitt';
import {UserSettings} from '../types/user-settings';

/**
 * Global settings persisted in the local storage.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export class Settings {
  // Version of the settings; if changed and the user's settings do not fit, the local storage is reset
  static readonly VERSION: number = 2;

  // Singleton instance
  private static _instance: Settings;

  // Main key in local storage to put settings into
  private _storageKey: string = 'sitepackage';

  // Contains the current user settings
  private _settings: UserSettings;

  // Event emitter
  private _emitter: Emitter<{ change: UserSettings }> = mitt<{ change: UserSettings }>();

  static get instance(): Settings {
    if (Settings._instance === undefined) {
      Settings._instance = new Settings();
    }
    return Settings._instance;
  }

  /**
   * Creates the local settings. Should load any required settings.
   */
  private constructor() {
    this._settings = this.load();
  }

  private get storageKey(): string {
    return this._storageKey;
  }

  public get settings(): UserSettings {
    return this._settings;
  }

  public set settings(settings: UserSettings) {
    this._settings = settings;
  }

  public get emitter(): Emitter<{ change: UserSettings }> {
    return this._emitter;
  }

  /**
   * Loads the settings from the local storage.
   *
   * @returns {UserSettings}
   */
  private load(): UserSettings {
    let settings: UserSettings = this.getDefaultSettings();

    // No settings exist yet
    const item = localStorage.getItem(this.storageKey);
    if (!item) {
      settings = this.reset();
    }

    // Try to fetch existing settings from the local storage
    else {
      try {
        if (item) {
          settings = JSON.parse(item);
        }

        // Sanity check
        if (settings !== null && !settings.hasOwnProperty('version')) {
          settings = this.reset();
        }
      } catch (e) {
        settings = this.reset();
      }
    }

    // If no settings could be retrieved, throw an error
    if (settings === null) {
      throw new Error('Could not fetch settings.');
    }

    // Check if the settings version is equal to the current version. If no, reset the settings.
    if (settings.version !== Settings.VERSION) {
      settings = this.reset();
    }
    return settings;
  }

  /**
   * Reloads the settings from the local storage.
   *
   * @returns {Settings}
   */
  public reload(): UserSettings {
    return this.load();
  }

  /**
   * Sets new settings in the local storage.
   *
   * @param {Settings} settings New settings
   */
  public set(settings: UserSettings): void {
    if (localStorage.getItem(this.storageKey) === undefined || !localStorage.getItem(this.storageKey)) {
      this.reset();
    }
    localStorage.setItem(this.storageKey, JSON.stringify(settings));
    this.settings = settings;
    this.emitter.emit('change', this.settings);
  }

  /**
   * Gets default settings.
   *
   * @returns {UserSettings}
   */
  public getDefaultSettings(): UserSettings {
    return {
      version: Settings.VERSION,
      timestamp: (new Date()).getTime(),
      filter: null
    };
  }

  /**
   * Resets the user settings in the local storage.
   *
   * @returns {UserSettings}
   */
  public reset(): UserSettings {
    const settings = this.getDefaultSettings();
    localStorage.setItem(this.storageKey, JSON.stringify(settings));
    this.settings = settings;
    this.emitter.emit('change', this.settings);
    return settings;
  }

  /**
   * Deletes the user settings from the local storage.
   *
   * @returns {UserSettings}
   */
  public delete(): UserSettings {
    const settings = this.getDefaultSettings();
    localStorage.removeItem(this.storageKey);
    this.settings = this.getDefaultSettings();
    this.emitter.emit('change', this.settings);
    return settings;
  }
}
